import { record } from 'rrweb';
import type { eventWithTime, listenerHandler } from '@rrweb/types';

export interface RecorderConfig {
  // How often to upload chunks (in seconds)
  uploadIntervalSeconds: number;
  // Mask all inputs by default
  maskAllInputs: boolean;
  // CSS selectors for elements to mask (in addition to defaults)
  maskSelectors: string[];
  // Callback when a recording chunk is ready to send
  onChunkReady: (events: eventWithTime[], metrics: ChunkMetrics) => void;
  // Callback when recording stops (for final flush)
  onStop: (events: eventWithTime[], metrics: ChunkMetrics) => void;
}

export interface ChunkMetrics {
  clickCount: number;
  pageViewCount: number;
}

const DEFAULT_CONFIG: RecorderConfig = {
  uploadIntervalSeconds: 30,
  maskAllInputs: false,
  maskSelectors: [],
  onChunkReady: () => {},
  onStop: () => {},
};

// Selectors for sensitive fields that should always be masked
const SENSITIVE_SELECTORS = [
  'input[type="password"]',
  'input[type="credit-card"]',
  'input[name*="password"]',
  'input[name*="secret"]',
  'input[name*="token"]',
  'input[name*="card"]',
  'input[name*="cvv"]',
  'input[name*="ssn"]',
  '[data-userlatch-mask]',
  '[data-sensitive]',
];

export class SessionRecorder {
  private config: RecorderConfig;
  private eventBuffer: eventWithTime[] = [];
  private isRecording = false;
  private stopFn: listenerHandler | null = null;
  private uploadTimer: ReturnType<typeof setInterval> | null = null;
  
  // Metrics tracking for current chunk
  private clickCount = 0;
  private pageViewCount = 0;
  
  // Total metrics for the session
  private totalClickCount = 0;
  private totalPageViewCount = 0;

  constructor(config: Partial<RecorderConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  start(): void {
    if (this.isRecording) return;

    const allMaskSelectors = [...SENSITIVE_SELECTORS, ...this.config.maskSelectors];

    this.stopFn = record({
      emit: (event) => this.handleEvent(event),
      maskAllInputs: this.config.maskAllInputs,
      maskInputOptions: {
        password: true,
      },
      maskTextSelector: allMaskSelectors.join(', '),
      maskInputFn: (text: string, element: HTMLElement | null) => {
        // Check if element matches sensitive selectors
        if (element instanceof HTMLElement) {
          for (const selector of allMaskSelectors) {
            if (element.matches(selector)) {
              return '*'.repeat(text.length);
            }
          }
        }
        return text;
      },
      // Reduce recording size by sampling mouse movements
      sampling: {
        mousemove: true,
        mouseInteraction: true,
        scroll: 150, // Throttle scroll events to every 150ms
        input: 'last', // Only record the last input value
      },
      // Don't record cross-origin iframes
      recordCrossOriginIframes: false,
    });

    this.isRecording = true;

    // Setup periodic upload
    this.uploadTimer = setInterval(() => {
      this.flushChunk();
    }, this.config.uploadIntervalSeconds * 1000);
  }

  stop(): void {
    // Stop the upload timer
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
      this.uploadTimer = null;
    }

    // Stop rrweb recording
    if (this.stopFn) {
      this.stopFn();
      this.stopFn = null;
    }

    // Final flush
    if (this.eventBuffer.length > 0) {
      const events = [...this.eventBuffer];
      const metrics: ChunkMetrics = {
        clickCount: this.clickCount,
        pageViewCount: this.pageViewCount,
      };
      this.config.onStop(events, metrics);
    }

    this.isRecording = false;
    this.eventBuffer = [];
    this.clickCount = 0;
    this.pageViewCount = 0;
  }

  /**
   * Increment click count (called from tracker when clicks are detected)
   */
  recordClick(): void {
    this.clickCount++;
    this.totalClickCount++;
  }

  /**
   * Increment page view count (called from tracker on navigation)
   */
  recordPageView(): void {
    this.pageViewCount++;
    this.totalPageViewCount++;
  }

  /**
   * Get current metrics
   */
  getMetrics(): { clickCount: number; pageViewCount: number; totalClickCount: number; totalPageViewCount: number } {
    return {
      clickCount: this.clickCount,
      pageViewCount: this.pageViewCount,
      totalClickCount: this.totalClickCount,
      totalPageViewCount: this.totalPageViewCount,
    };
  }

  private handleEvent(event: eventWithTime): void {
    this.eventBuffer.push(event);
  }

  private flushChunk(): void {
    if (this.eventBuffer.length === 0) return;

    // Clone events before sending
    const events = [...this.eventBuffer];
    const metrics: ChunkMetrics = {
      clickCount: this.clickCount,
      pageViewCount: this.pageViewCount,
    };

    // Clear the buffer and reset chunk metrics
    this.eventBuffer = [];
    this.clickCount = 0;
    this.pageViewCount = 0;

    // Send to callback
    this.config.onChunkReady(events, metrics);
  }

  /**
   * Force flush the current buffer (e.g., on page unload)
   */
  forceFlush(): { events: eventWithTime[]; metrics: ChunkMetrics } | null {
    if (this.eventBuffer.length === 0) return null;

    const events = [...this.eventBuffer];
    const metrics: ChunkMetrics = {
      clickCount: this.clickCount,
      pageViewCount: this.pageViewCount,
    };

    // Clear the buffer and reset chunk metrics
    this.eventBuffer = [];
    this.clickCount = 0;
    this.pageViewCount = 0;

    return { events, metrics };
  }

  /**
   * Get the current buffer for inspection
   */
  getBuffer(): eventWithTime[] {
    return [...this.eventBuffer];
  }

  /**
   * Check if recorder is active
   */
  isActive(): boolean {
    return this.isRecording;
  }
}
